import { useEffect, type RefObject } from "react";
import { create } from "zustand";
import {
  completionReadCursor,
  mergeThreadCompletionReads,
  parseThreadCompletionReads,
  type ThreadCompletionRead,
} from "@cleo/contracts";
import { useNeucleosWorldStore } from "./worldStore";
import { postLocalJson } from "./localJsonTransport";

const STORAGE_KEY = "cleo:completion-reads:v1";
export type CompletionScope = { identity: string; worldKey: string; actorId?: string } | null;
interface Bucket {
  entries: ThreadCompletionRead[];
  pending: ThreadCompletionRead[];
  actorId?: string;
}
type Buckets = Record<string, Bucket>;
export const completionScopeKey = (scope: CompletionScope) =>
  scope === null
    ? "local"
    : JSON.stringify([scope.identity, scope.worldKey, scope.actorId ?? null]);
function restore(): Buckets {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}");
    if (!value || typeof value !== "object") return {};
    const buckets: Buckets = {};
    for (const [key, raw] of Object.entries(value)) {
      if (!raw || typeof raw !== "object") continue;
      const b = raw as Bucket;
      const entries = parseThreadCompletionReads(b.entries),
        pending = parseThreadCompletionReads(b.pending);
      if (entries && pending)
        buckets[key] = {
          entries,
          pending,
          ...(typeof b.actorId === "string" ? { actorId: b.actorId } : {}),
        };
    }
    return buckets;
  } catch {
    return {};
  }
}
export const useCompletionReadsStore = create<{ buckets: Buckets }>(() => ({ buckets: restore() }));
useCompletionReadsStore.subscribe(({ buckets }, previous) => {
  if (buckets === previous.buckets) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(buckets));
  } catch {
    /* Local fallback remains in memory if storage is unavailable. */
  }
});
const emptyBucket = (): Bucket => ({ entries: [], pending: [] });
export function currentCompletionScope(
  worldKey: string | null | undefined,
): CompletionScope | undefined {
  if (!worldKey) return null;
  const state = useNeucleosWorldStore.getState();
  return state.signedIn && state.identity
    ? {
        identity: state.identity,
        worldKey,
        ...(state.worlds.find((world) => world.worldKey === worldKey)?.actorId
          ? { actorId: state.worlds.find((world) => world.worldKey === worldKey)!.actorId! }
          : {}),
      }
    : undefined;
}
export function completionCursor(
  buckets: Buckets,
  scope: CompletionScope | undefined,
  threadKey: string,
): string | undefined {
  return scope === undefined
    ? undefined
    : completionReadCursor(
        buckets[completionScopeKey(scope)]?.entries.find((entry) => entry.threadKey === threadKey),
      );
}
function record(scope: CompletionScope, entry: ThreadCompletionRead) {
  const key = completionScopeKey(scope);
  useCompletionReadsStore.setState(({ buckets }) => {
    const old = buckets[key] ?? emptyBucket();
    const entries = parseThreadCompletionReads(mergeThreadCompletionReads(old.entries, [entry]));
    const pending =
      scope === null
        ? []
        : parseThreadCompletionReads(mergeThreadCompletionReads(old.pending, [entry]));
    if (!entries || !pending) return { buckets };
    return { buckets: { ...buckets, [key]: { ...old, entries, pending } } };
  });
}
export function acknowledgeCompletion(
  scope: CompletionScope,
  threadKey: string,
  completedAt: string,
) {
  if (!Number.isFinite(Date.parse(completedAt))) return;
  const old = useCompletionReadsStore
    .getState()
    .buckets[completionScopeKey(scope)]?.entries.find((entry) => entry.threadKey === threadKey);
  record(scope, {
    threadKey,
    completedAt,
    observedAt: Math.max(Date.now(), (old?.unreadAt ?? 0) + 1, old?.observedAt ?? 0),
  });
}
export function markCompletionUnread(
  worldKey: string | null | undefined,
  threadKey: string,
  completedAt: string | null | undefined,
) {
  const scope = currentCompletionScope(worldKey);
  if (scope === undefined || !completedAt || !Number.isFinite(Date.parse(completedAt))) return;
  const old = useCompletionReadsStore
    .getState()
    .buckets[completionScopeKey(scope)]?.entries.find((entry) => entry.threadKey === threadKey);
  record(scope, {
    threadKey,
    unreadCompletedAt: completedAt,
    unreadAt: Math.max(Date.now(), (old?.observedAt ?? 0) + 1, (old?.unreadAt ?? 0) + 1),
  });
}

/** A receipt is accepted only from a supported authenticated view-pref readback. */
async function call(
  scope: Exclude<CompletionScope, null>,
  pending: ThreadCompletionRead[] | null,
  actorId: string | undefined,
  fetchImpl: typeof fetch,
) {
  try {
    const response = await postLocalJson(
      pending === null ? "/api/neucleos/world/view-prefs" : "/api/neucleos/world/view-prefs/save",
      {
        worldKey: scope.worldKey,
        requesterIdentity: scope.identity,
        ...(actorId ? { expectedActorId: actorId } : {}),
        ...(pending === null ? {} : { completionReads: pending }),
      },
      fetchImpl,
    );
    if (response.status === 401 || response.status === 403) return "refused" as const;
    const body = await response.json();
    const entries = parseThreadCompletionReads(body.completionReads);
    if (
      !response.ok ||
      body.ok !== true ||
      body.completionReadsSupported !== true ||
      typeof body.actorId !== "string" ||
      !body.actorId ||
      !entries ||
      (actorId && actorId !== body.actorId)
    )
      return null;
    return { entries, actorId: body.actorId as string };
  } catch {
    return null;
  }
}
export async function syncCompletionScope(
  scope: Exclude<CompletionScope, null>,
  stillCurrent: () => boolean,
  fetchImpl: typeof fetch = fetch,
): Promise<"synced" | "retry" | "refused" | "obsolete"> {
  const key = completionScopeKey(scope);
  const read = await call(scope, null, scope.actorId, fetchImpl);
  if (!stillCurrent()) return "obsolete";
  if (read === "refused") return "refused";
  if (!read) return "retry";
  useCompletionReadsStore.setState(({ buckets }) => {
    const bucket = buckets[key] ?? emptyBucket();
    // Actor replacement under the same login must never upload or project the
    // former Actor's receipts. Retain that Actor's offline queue separately.
    const replaced = bucket.actorId !== undefined && bucket.actorId !== read.actorId;
    const previous = replaced
      ? { [JSON.stringify([scope.identity, scope.worldKey, bucket.actorId])]: bucket }
      : {};
    const own = replaced ? emptyBucket() : bucket;
    const entries =
      parseThreadCompletionReads(mergeThreadCompletionReads(own.entries, read.entries)) ??
      own.entries;
    if (
      !replaced &&
      bucket.actorId === read.actorId &&
      JSON.stringify(entries) === JSON.stringify(bucket.entries)
    )
      return { buckets };
    return {
      buckets: {
        ...buckets,
        ...previous,
        [key]: {
          ...own,
          actorId: read.actorId,
          entries,
        },
      },
    };
  });
  const submitted = useCompletionReadsStore.getState().buckets[key]?.pending ?? [];
  if (!stillCurrent()) return "obsolete";
  if (!submitted.length) return "synced";
  // Bounded patches fit the local HTTP request limit; remaining receipts retry.
  const patch = submitted.slice(0, 24);
  const saved = await call(scope, patch, read.actorId, fetchImpl);
  if (!stillCurrent()) return "obsolete";
  if (saved === "refused") return "refused";
  if (!saved) return "retry";
  useCompletionReadsStore.setState(({ buckets }) => {
    const bucket = buckets[key] ?? emptyBucket();
    const pending = bucket.pending.filter((entry) => {
      const sent = patch.find((item) => item.threadKey === entry.threadKey);
      if (JSON.stringify(sent) !== JSON.stringify(entry)) return true;
      const remote = saved.entries.find((item) => item.threadKey === entry.threadKey);
      return (
        !remote ||
        JSON.stringify(mergeThreadCompletionReads([remote], [entry])[0]) !== JSON.stringify(remote)
      );
    });
    return {
      buckets: {
        ...buckets,
        [key]: {
          ...bucket,
          entries:
            parseThreadCompletionReads(mergeThreadCompletionReads(bucket.entries, saved.entries)) ??
            bucket.entries,
          pending,
        },
      },
    };
  });
  return "synced";
}
/** Unsent receipts retry from here, doubling up to the cap; a drained queue arms nothing. */
export const COMPLETION_READ_RETRY_BASE_MS = 5_000;
export const COMPLETION_READ_RETRY_MAX_MS = 60_000;
/** Focus and visibility landing together, or a run of alt-tabs, cost one read. */
export const COMPLETION_READ_RETURN_MIN_INTERVAL_MS = 10_000;

/** Whether `now` holds a receipt `before` did not: what a send must carry. */
function gainedPending(
  now: ReadonlyArray<ThreadCompletionRead> | undefined,
  before: ReadonlyArray<ThreadCompletionRead> | undefined,
): boolean {
  if (!now?.length) return false;
  const known = new Set((before ?? []).map((entry) => JSON.stringify(entry)));
  return now.some((entry) => !known.has(JSON.stringify(entry)));
}

/**
 * Keep this Actor's completion receipts true in every entitled World without
 * a standing poll. Every read rides the view-prefs door, so reads happen only
 * at the moments the answer can differ: arriving (mount, a grant change),
 * coming back to the window (focus, visibility, online), and a receipt of our
 * own to send (a bucket gaining pending entries). Unsent receipts — offline, a
 * failed save, a save that did not dominate — retry with backoff; an
 * authorization refusal waits for a changed grant. A drained queue arms
 * nothing, so an idle Desk sends nothing.
 */
export function useCompletionReadSync() {
  const identity = useNeucleosWorldStore((state) => state.identity);
  const signedIn = useNeucleosWorldStore((state) => state.signedIn);
  // The World SET, not the array: a re-hydrate that changes no World must not
  // restart the sync, which would itself be a read per hydrate.
  const worldsKey = useNeucleosWorldStore((state) =>
    JSON.stringify(state.worlds.map((world) => [world.worldKey, world.actorId ?? null])),
  );
  const revision = useNeucleosWorldStore((state) => state.grantRevision);
  useEffect(() => {
    if (!signedIn || !identity) return;
    const scopes = (JSON.parse(worldsKey) as Array<[string, string | null]>).map(
      ([worldKey, actorId]): Exclude<CompletionScope, null> => ({
        identity,
        worldKey,
        ...(actorId ? { actorId } : {}),
      }),
    );
    let active = true,
      running = false,
      lastStartedAt = Number.NEGATIVE_INFINITY,
      retryDelay = COMPLETION_READ_RETRY_BASE_MS,
      retryTimer: number | undefined;
    const queued = new Map<string, { scope: Exclude<CompletionScope, null>; refresh: boolean }>();
    const refused = new Set<string>();
    const current = () =>
      active &&
      useNeucleosWorldStore.getState().signedIn &&
      useNeucleosWorldStore.getState().identity === identity &&
      useNeucleosWorldStore.getState().grantRevision === revision;
    const clearRetry = () => {
      if (retryTimer === undefined) return;
      window.clearTimeout(retryTimer);
      retryTimer = undefined;
    };
    const unsentScopes = () =>
      scopes.filter(
        (scope) =>
          !refused.has(completionScopeKey(scope)) &&
          (useCompletionReadsStore.getState().buckets[completionScopeKey(scope)]?.pending.length ??
            0) > 0,
      );
    const sync = async (
      requested: ReadonlyArray<Exclude<CompletionScope, null>>,
      refresh = false,
    ): Promise<void> => {
      if (!current()) return;
      requested = requested.filter((scope) => !refused.has(completionScopeKey(scope)));
      if (running) {
        for (const scope of requested) {
          const key = completionScopeKey(scope);
          queued.set(key, { scope, refresh: refresh || queued.get(key)?.refresh === true });
        }
        return;
      }
      if (!requested.length) return;
      running = true;
      lastStartedAt = Date.now();
      clearRetry();
      try {
        await Promise.all(
          requested.map(async (scope) => {
            if ((await syncCompletionScope(scope, current)) === "refused")
              refused.add(completionScopeKey(scope));
          }),
        );
      } finally {
        running = false;
      }
      if (!current()) return;
      if (queued.size) {
        // A receipt arriving during the read may already have ridden its save.
        // Repeat only an explicit refresh or receipts the readback still lacks.
        const stillPending = new Set(unsentScopes().map(completionScopeKey));
        const next = [...queued.values()]
          .filter(({ scope, refresh }) => refresh || stillPending.has(completionScopeKey(scope)))
          .map(({ scope }) => scope);
        queued.clear();
        if (next.length) {
          void sync(next);
          return;
        }
      }
      if (!unsentScopes().length) {
        retryDelay = COMPLETION_READ_RETRY_BASE_MS;
        return;
      }
      retryTimer = window.setTimeout(() => {
        retryTimer = undefined;
        void sync(unsentScopes());
      }, retryDelay);
      retryDelay = Math.min(retryDelay * 2, COMPLETION_READ_RETRY_MAX_MS);
    };
    // A receipt of our own rides at once.
    const send = (requested: ReadonlyArray<Exclude<CompletionScope, null>>, refresh = false) => {
      retryDelay = COMPLETION_READ_RETRY_BASE_MS;
      void sync(requested, refresh);
    };
    // Coming back to the window: another device may have read since we last
    // looked. Bounded so the moments arriving together cost one read; unsent
    // receipts always go.
    const back = () => {
      if (
        !unsentScopes().length &&
        Date.now() - lastStartedAt < COMPLETION_READ_RETURN_MIN_INTERVAL_MS
      )
        return;
      send(scopes, true);
    };
    const shown = () => {
      if (document.visibilityState === "visible") back();
    };
    const unsubscribe = useCompletionReadsStore.subscribe((state, previous) => {
      if (state.buckets === previous.buckets) return;
      const gained = scopes.filter((scope) => {
        const key = completionScopeKey(scope);
        return gainedPending(state.buckets[key]?.pending, previous.buckets[key]?.pending);
      });
      if (gained.length) send(gained);
    });
    void sync(scopes, true);
    window.addEventListener("online", back);
    window.addEventListener("focus", back);
    document.addEventListener("visibilitychange", shown);
    return () => {
      active = false;
      clearRetry();
      unsubscribe();
      window.removeEventListener("online", back);
      window.removeEventListener("focus", back);
      document.removeEventListener("visibilitychange", shown);
    };
  }, [identity, signedIn, worldsKey, revision]);
}

/** Exposure checks the actual rendered result, including clipping and overlays. */
export function resultIsVisible(element: HTMLElement): boolean {
  if (!element.isConnected || !element.getClientRects().length) return false;
  let rect = element.getBoundingClientRect();
  let left = Math.max(0, rect.left),
    top = Math.max(0, rect.top),
    right = Math.min(window.innerWidth, rect.right),
    bottom = Math.min(window.innerHeight, rect.bottom);
  for (let parent: HTMLElement | null = element; parent; parent = parent.parentElement) {
    const style = getComputedStyle(parent);
    if (
      parent.hidden ||
      parent.getAttribute("aria-hidden") === "true" ||
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.opacity === "0"
    )
      return false;
    if (
      parent !== element &&
      /(hidden|clip|auto|scroll)/.test(style.overflow + style.overflowX + style.overflowY)
    ) {
      rect = parent.getBoundingClientRect();
      left = Math.max(left, rect.left);
      right = Math.min(right, rect.right);
      top = Math.max(top, rect.top);
      bottom = Math.min(bottom, rect.bottom);
    }
  }
  if (right - left < 16 || bottom - top < 16) return false;
  const hit = document.elementFromPoint((left + right) / 2, (top + bottom) / 2);
  return hit !== null && element.contains(hit);
}
export function useVisibleCompletion(input: {
  containerRef: RefObject<HTMLElement | null>;
  threadKey: string | null;
  worldKey: string | null | undefined;
  completedAt: string | null | undefined;
  messageId: string | null | undefined;
  enabled: boolean;
}) {
  const identity = useNeucleosWorldStore((s) => s.identity),
    signedIn = useNeucleosWorldStore((s) => s.signedIn);
  const { containerRef, threadKey, worldKey, completedAt, messageId, enabled } = input;
  const actorId = useNeucleosWorldStore(
    (s) => s.worlds.find((world) => world.worldKey === worldKey)?.actorId,
  );
  useEffect(() => {
    if (
      !enabled ||
      !threadKey ||
      !completedAt ||
      !messageId ||
      !Number.isFinite(Date.parse(completedAt))
    )
      return;
    const scope = currentCompletionScope(worldKey);
    if (scope === undefined) return;
    let acknowledged = false;
    const inspect = () => {
      if (
        acknowledged ||
        JSON.stringify(currentCompletionScope(worldKey)) !== JSON.stringify(scope)
      )
        return;
      const container = containerRef.current;
      const element = container
        ? [
            ...container.querySelectorAll<HTMLElement>(
              "[data-message-id][data-message-role='assistant']",
            ),
          ].find((row) => row.dataset.messageId === messageId)
        : undefined;
      const content = element?.querySelector<HTMLElement>("[data-readback-content]");
      const visible =
        document.visibilityState === "visible" &&
        document.hasFocus() &&
        content != null &&
        resultIsVisible(content);
      if (!visible) return;
      if (!acknowledged) {
        acknowledged = true;
        acknowledgeCompletion(scope, threadKey, completedAt);
      }
    };
    // Layout/virtualization can mount a result after ChatView renders. Polling
    // checks live geometry and avoids treating queued observer entries as views.
    const timer = window.setInterval(inspect, 250);
    const observer = new MutationObserver(inspect);
    if (containerRef.current)
      observer.observe(containerRef.current, { subtree: true, childList: true, attributes: true });
    document.addEventListener("visibilitychange", inspect);
    window.addEventListener("focus", inspect);
    window.addEventListener("blur", inspect);
    window.addEventListener("scroll", inspect, true);
    inspect();
    return () => {
      window.clearInterval(timer);
      observer.disconnect();
      document.removeEventListener("visibilitychange", inspect);
      window.removeEventListener("focus", inspect);
      window.removeEventListener("blur", inspect);
      window.removeEventListener("scroll", inspect, true);
    };
  }, [
    containerRef,
    threadKey,
    worldKey,
    completedAt,
    messageId,
    enabled,
    identity,
    signedIn,
    actorId,
  ]);
}
